import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useAppDispatch } from "../store/hooks";
import { fetchEventsData } from "../store/events-actions";

// Web and mobile share one Firebase database, so a change made on one client
// would otherwise sit invisible on the other until a manual reload. This
// re-pulls events when the tab regains attention, plus a slow poll while it
// stays open.
const POLL_INTERVAL_MS = 60_000;
// Ignores a trigger that lands right after a fetch, so tab-switching back and
// forth doesn't hammer the database.
const MIN_GAP_MS = 15_000;

export const useBackgroundRefresh = (isAuth: string | null) => {
  const dispatch = useAppDispatch();
  const { uid } = useSelector((state: RootState) => state.auth);
  const { isRefreshing, addEvent } = useSelector(
    (state: RootState) => state.ui
  );
  const { eventToEdit } = useSelector((state: RootState) => state.events);

  const lastFetchRef = useRef(0);
  // Kept in refs so the effect below doesn't re-subscribe on every change.
  const stateRef = useRef({
    uid,
    isAuthed: !!isAuth,
    isRefreshing,
    isFormOpen: false,
  });
  stateRef.current = {
    uid,
    isAuthed: !!isAuth,
    isRefreshing,
    // Replacing the events list while the user has a form open could yank the
    // record out from under them, so hold off until they're done.
    isFormOpen: addEvent || eventToEdit !== null,
  };

  const refresh = useCallback(() => {
    const {
      uid: u,
      isAuthed: authed,
      isRefreshing: busy,
      isFormOpen,
    } = stateRef.current;
    if (!authed || !u || busy || isFormOpen) {
      return;
    }
    if (Date.now() - lastFetchRef.current < MIN_GAP_MS) {
      return;
    }
    lastFetchRef.current = Date.now();
    dispatch(fetchEventsData(u, true));
  }, [dispatch]);

  useEffect(() => {
    if (!isAuth) {
      return;
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    const poll = setInterval(() => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    }, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(poll);
    };
  }, [isAuth, refresh]);
};

export default useBackgroundRefresh;
